<template>
  <v-card>
    <v-card-title>
      <div>
        <div class="headline">Push Mail</div>
      </div>
      <v-spacer />
      <v-btn color="primary" @click="composeDialog = true">
        <v-icon left small color="white">fal fa-paper-plane</v-icon>
        Compose
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="mails"
        :loading="loading"
        hide-default-footer
        disable-sort
      >
        <template v-slot:item.recipient_type="{ item }">
          <v-chip small outlined>{{ item.recipient_type }}</v-chip>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small :color="statusColor(item.status)" dark>
            {{ item.status }}
          </v-chip>
        </template>

        <template v-slot:item.sent_count="{ item }">
          {{ item.sent_count }} / {{ item.recipient_count }}
          <span v-if="item.failed_count > 0" class="red--text ml-1">
            ({{ item.failed_count }} failed)
          </span>
        </template>

        <template v-slot:item.sentBy="{ item }">
          {{ item.sentBy ? item.sentBy.first_name + ' ' + item.sentBy.last_name : '—' }}
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="viewDetail(item)">
            <v-icon small>fal fa-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Compose dialog -->
    <push-mail-compose-dialog
      v-model="composeDialog"
      @sent="onSent"
    />

    <!-- Detail dialog -->
    <v-dialog v-model="detailDialog" max-width="700">
      <v-card v-if="selected">
        <v-card-title>Push Mail Detail</v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <tbody>
              <tr><td><strong>Subject</strong></td><td>{{ selected.subject }}</td></tr>
              <tr><td><strong>Template</strong></td><td><code>{{ selected.template_id }}</code></td></tr>
              <tr><td><strong>Recipients</strong></td><td>{{ selected.recipient_type }}</td></tr>
              <tr><td><strong>Countries</strong></td>
                  <td>{{ (selected.filters && selected.filters.country_codes || []).join(', ') || 'All' }}</td></tr>
              <tr><td><strong>Sent</strong></td><td>{{ selected.sent_count }} / {{ selected.recipient_count }}</td></tr>
              <tr><td><strong>Status</strong></td><td>{{ selected.status }}</td></tr>
              <tr><td><strong>Sent by</strong></td>
                  <td>{{ selected.sentBy ? selected.sentBy.first_name + ' ' + selected.sentBy.last_name : '—' }}</td></tr>
              <tr><td><strong>Sent at</strong></td><td>{{ formatDate(selected.created_at) }}</td></tr>
            </tbody>
          </v-simple-table>
          <v-divider class="my-3" />
          <div class="caption grey--text mb-1">Body content</div>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="body-preview" v-html="selected.body_content" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import PushMailComposeDialog from "../dialogs/PushMailComposeDialog";

export default {
  name: "PushMailPage",
  components: { PushMailComposeDialog },

  data: () => ({
    loading: true,
    mails: [],
    composeDialog: false,
    detailDialog: false,
    selected: null,
    pollTimer: null,

    headers: [
      { text: "Subject",       value: "subject" },
      { text: "Type",          value: "recipient_type" },
      { text: "Sent / Total",  value: "sent_count" },
      { text: "Status",        value: "status" },
      { text: "Sent by",       value: "sentBy" },
      { text: "Date",          value: "created_at" },
      { text: "",              value: "actions", align: "right" },
    ],
  }),

  methods: {
    ...mapActions("moderator", ["getAdminPushMails"]),

    async load(silent = false) {
      if (!silent) this.loading = true;
      try {
        const mails = await this.getAdminPushMails({ limit: 50 });
        this.mails = mails;
        this.syncDetailDialog();
        this.managePolling();
      } finally {
        if (!silent) this.loading = false;
      }
    },

    syncDetailDialog() {
      if (this.selected) {
        const updated = this.mails.find(m => m.uuid === this.selected.uuid);
        if (updated) this.selected = updated;
      }
    },

    managePolling() {
      const hasActive = this.mails.some(m => m.status === "pending" || m.status === "sending");
      if (hasActive && !this.pollTimer) {
        this.pollTimer = setInterval(() => this.load(true), 3000);
      } else if (!hasActive && this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    statusColor(status) {
      return { pending: "grey", sending: "orange", completed: "green", failed: "red" }[status] ?? "grey";
    },

    formatDate(dt) {
      if (!dt) return "—";
      return new Date(dt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
    },

    viewDetail(item) {
      this.selected = item;
      this.detailDialog = true;
    },

    onSent(result) {
      this.$toast?.success?.(`Queued for ${result.recipient_count} recipients.`);
      this.load();
    },
  },

  created() {
    this.load();
  },

  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
};
</script>

<style scoped>
.body-preview {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 13px;
}
</style>